import React, { useEffect, useState, useRef } from 'react';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
import { useNavigate, useLocation } from 'react-router-dom';
import { SendIcon, PaperclipIcon, ImageIcon, FileIcon, XIcon, ChevronLeftIcon, UserIcon, PhoneIcon, VideoIcon, InfoIcon, MessageSquareIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../providers/ThemeProvider';
import { Conversation, Message, getUserConversations, subscribeToUserConversations, subscribeToConversationMessages, sendMessage, markMessagesAsRead, createConversation, getTeacherById, getConversation } from '../../services/messagingService';
export const StudentMessages = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    currentUser
  } = useAuth();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [optimisticMessages, setOptimisticMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const activeConversationIdRef = useRef<string | null>(null);
  const [isMobileView, setIsMobileView] = useState(false);
  const [showConversationList, setShowConversationList] = useState(true);
  const [isSending, setIsSending] = useState(false);
  // NEW: Track user scroll position to avoid interrupting manual scrolling
  const userScrollPositionRef = useRef<{
    scrollTop: number;
    scrollHeight: number;
    clientHeight: number;
    isNearBottom: boolean;
    hasManuallyScrolled: boolean;
  }>({
    scrollTop: 0,
    scrollHeight: 0,
    clientHeight: 0,
    isNearBottom: true,
    hasManuallyScrolled: false
  });
  // Flag to track if we should force scroll after sending
  const shouldForceScrollRef = useRef(false);
  // NEW: Use a single scroll operation ID to prevent race conditions
  const scrollOperationIdRef = useRef(0);
  // Track last message count to determine if new messages arrived
  const lastMessageCountRef = useRef(0);
  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);
  // Check for teacherId in query params (for direct messaging from teacher profile)
  const queryParams = new URLSearchParams(location.search);
  const teacherIdParam = queryParams.get('teacherId');
  // NEW: Better mobile state tracking
  const [mobileState, setMobileState] = useState({
    isKeyboardOpen: false,
    isTyping: false,
    viewportHeight: window.innerHeight // Track viewport height for keyboard detection
  });
  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobileView(mobile);
      // NEW: Update viewport height on resize
      if (mobile) {
        setMobileState(prev => ({
          ...prev,
          viewportHeight: window.innerHeight
        }));
      }
    };
    handleResize(); // Set initial value
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  // Set mounted ref for cleanup
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  // Handle direct teacher contact from profile page
  const handleTeacherContact = async (teacherId: string) => {
    try {
      console.log('Starting teacher contact process with teacherId:', teacherId);
      // Check if we already have a conversation with this teacher
      const existingConversation = conversations.find(conv => conv.teacherId === teacherId);
      if (existingConversation) {
        console.log('Found existing conversation:', existingConversation.id);
        // Use existing conversation
        handleSelectConversation(existingConversation);
      } else {
        console.log('No existing conversation found, creating new one');
        // Create a new conversation
        const teacher = await getTeacherById(teacherId);
        if (!teacher) {
          console.error('Teacher not found with ID:', teacherId);
          setError('Teacher not found');
          return;
        }
        console.log('Teacher data retrieved:', teacher.fullName);
        // Create conversation with proper fallbacks for all required fields
        const conversationId = await createConversation(teacherId, teacher.fullName || 'Teacher', teacher.profilePictureUrl || '', currentUser.uid, currentUser.displayName || 'Student', currentUser.photoURL || '', teacher.instruments && teacher.instruments.length > 0 ? teacher.instruments[0] : 'Music');
        console.log('New conversation created with ID:', conversationId);
        // Refresh conversations to include the new one
        const updatedConversations = await getUserConversations(currentUser.uid);
        setConversations(updatedConversations);
        // Find and select the new conversation
        const newConversation = updatedConversations.find(conv => conv.id === conversationId);
        if (newConversation) {
          console.log('Found and selecting new conversation');
          handleSelectConversation(newConversation);
        } else {
          console.error('Created conversation not found in updated list');
          try {
            // Force a direct fetch of the conversation to handle any timing issues
            const directFetchedConversation = await getConversation(conversationId);
            if (directFetchedConversation) {
              setConversations([...updatedConversations, directFetchedConversation]);
              handleSelectConversation(directFetchedConversation);
            }
          } catch (fetchErr) {
            console.error('Error fetching new conversation:', fetchErr);
            // Handle the error gracefully without crashing the component
            setError('Error loading conversation. Please try refreshing the page.');
          }
        }
      }
      // On mobile, explicitly hide the conversation list to show the message area
      if (isMobileView) {
        setShowConversationList(false);
      }
    } catch (err) {
      console.error('Error handling teacher contact:', err);
      setError('Failed to start conversation with teacher. Please try again.');
    }
  };
  // Fetch conversations on component mount
  useEffect(() => {
    if (!currentUser) {
      navigate('/student-login');
      return;
    }
    const fetchConversations = async () => {
      try {
        setLoading(true);
        setError(''); // Clear any previous errors
        console.log('Fetching conversations for user:', currentUser.uid);
        // Verify user object has required properties
        if (!currentUser.uid) {
          console.error('Current user missing UID');
          setError('Authentication error. Please try logging in again.');
          setLoading(false);
          return;
        }
        // Get conversations first
        const userConversations = await getUserConversations(currentUser.uid);
        console.log('Fetched conversations:', userConversations.length);
        // Log conversation details for debugging
        userConversations.forEach((conv, index) => {
          console.log(`Conversation ${index + 1} details:`, {
            id: conv.id || 'MISSING ID',
            teacherId: conv.teacherId || 'MISSING TEACHER ID',
            teacherName: conv.teacherName || 'MISSING TEACHER NAME',
            studentId: conv.studentId || 'MISSING STUDENT ID',
            studentName: conv.studentName || 'MISSING STUDENT NAME',
            lastMessage: conv.lastMessage || '(No messages yet)'
          });
        });
        // Filter out invalid conversations
        const validConversations = userConversations.filter(conv => conv.id && conv.teacherId && conv.studentId);
        if (validConversations.length < userConversations.length) {
          console.warn(`Filtered out ${userConversations.length - validConversations.length} invalid conversations`);
        }
        // IMPROVED: Make sure conversations are sorted by lastMessageTimestamp (newest first)
        const sortedConversations = sortConversationsByTimestamp(validConversations);
        // Always set conversations (even if empty) and clear loading state
        if (isMountedRef.current) {
          setConversations(sortedConversations);
          setLoading(false);
        }
        // If there's a teacherId in the URL, handle it after conversations are loaded
        if (teacherIdParam) {
          console.log('Found teacherId in URL params:', teacherIdParam);
          handleTeacherContact(teacherIdParam);
        }
      } catch (err) {
        console.error('Error fetching conversations:', err);
        // More detailed error logging
        if (err instanceof Error) {
          console.error('Error details:', err.message);
          console.error('Error stack:', err.stack);
        }
        if (isMountedRef.current) {
          setError('Failed to load conversations. Please try again.');
          setLoading(false); // Make sure to set loading to false even on error
        }
      }
    };
    fetchConversations();
    // Set up subscription after initial fetch
    let unsubscribe: (() => void) | null = null;
    try {
      unsubscribe = subscribeToUserConversations(currentUser.uid, fetchedConversations => {
        console.log('Subscription update - conversations:', fetchedConversations.length);
        // Filter out invalid conversations
        const validConversations = fetchedConversations.filter(conv => conv.id && conv.teacherId && conv.studentId);
        if (validConversations.length < fetchedConversations.length) {
          console.warn(`Filtered out ${fetchedConversations.length - validConversations.length} invalid conversations from real-time update`);
        }
        // IMPROVED: Make sure conversations are sorted by lastMessageTimestamp (newest first)
        const sortedConversations = sortConversationsByTimestamp(validConversations);
        // Always update conversations (even if empty)
        if (isMountedRef.current) {
          setConversations(sortedConversations);
          // Clear any error if we successfully got an update
          if (error) setError('');
          // Ensure loading is set to false on subscription update
          if (loading) setLoading(false);
        }
      });
    } catch (subErr) {
      console.error('Error setting up conversation subscription:', subErr);
      // Set loading to false if subscription fails
      if (isMountedRef.current && loading) {
        setLoading(false);
      }
    }
    // Safety timeout to ensure loading state doesn't get stuck
    const safetyTimeout = setTimeout(() => {
      if (isMountedRef.current && loading) {
        console.log('Safety timeout triggered - forcing loading state to false');
        setLoading(false);
      }
    }, 5000); // 5 second safety timeout
    return () => {
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
      }
      clearTimeout(safetyTimeout);
    };
  }, [currentUser, navigate, teacherIdParam]); // Removed error and loading from dependencies
  // NEW: Helper function to sort conversations by timestamp
  const sortConversationsByTimestamp = (conversations: Conversation[]): Conversation[] => {
    return [...conversations].sort((a, b) => {
      // Get timestamp values, properly handling Firestore timestamps
      const getTimestampValue = (timestamp: any): number => {
        // For Firestore timestamps with toDate method
        if (timestamp && typeof timestamp === 'object' && timestamp.toDate) {
          try {
            return timestamp.toDate().getTime();
          } catch (e) {
            console.error('Error converting Firestore timestamp:', e);
            return 0;
          }
        }
        // For Date objects
        if (timestamp instanceof Date) {
          return timestamp.getTime();
        }
        // For numeric timestamps
        if (typeof timestamp === 'number') {
          return timestamp;
        }
        // Default case - use 0 to ensure consistent sorting
        return 0;
      };
      const timeA = getTimestampValue(a.lastMessageTimestamp);
      const timeB = getTimestampValue(b.lastMessageTimestamp);
      // Sort in descending order (newest first)
      return timeB - timeA;
    });
  };
  // Subscribe to messages when a conversation is selected
  useEffect(() => {
    if (!selectedConversation) {
      setMessages([]);
      setOptimisticMessages([]);
      activeConversationIdRef.current = null;
      return;
    }
    // Validate the selected conversation has all required fields
    if (!selectedConversation.id || !selectedConversation.teacherId) {
      console.error('Selected conversation is missing critical fields:', selectedConversation);
      setError('Invalid conversation data. Please try selecting another conversation.');
      return;
    }
    console.log('Setting up message subscription for conversation:', selectedConversation.id);
    // Ensure the active conversation ID is set properly
    activeConversationIdRef.current = selectedConversation.id;
    // NEW: Reset scroll position tracking when conversation changes
    userScrollPositionRef.current = {
      scrollTop: 0,
      scrollHeight: 0,
      clientHeight: 0,
      isNearBottom: true,
      hasManuallyScrolled: false
    };
    const unsubscribe = subscribeToConversationMessages(selectedConversation.id, fetchedMessages => {
      console.log(`Received ${fetchedMessages.length} messages for conversation ${selectedConversation.id}`);
      // Only update if this is still the active conversation and component is mounted
      if (activeConversationIdRef.current === selectedConversation.id && isMountedRef.current) {
        // Check if new messages arrived
        const newMessagesArrived = fetchedMessages.length > lastMessageCountRef.current;
        // Update the message count reference
        lastMessageCountRef.current = fetchedMessages.length;
        // Better handling of message merging
        setMessages(prevMessages => {
          // Get the IDs of real messages from the server
          const serverMessageIds = fetchedMessages.map(msg => msg.id);
          // Filter optimistic messages that haven't been synced to the server yet
          const pendingOptimisticMessages = optimisticMessages.filter(msg => typeof msg.id === 'string' && msg.id.startsWith('temp-') && !serverMessageIds.includes(msg.id));
          // Combine server messages with pending optimistic messages
          const combinedMessages = [...fetchedMessages];
          // Only add optimistic messages if they don't have matching IDs
          if (pendingOptimisticMessages.length > 0) {
            combinedMessages.push(...pendingOptimisticMessages);
          }
          // Sort the combined messages with improved sorting
          const sortedMessages = sortMessagesByTimestamp(combinedMessages);
          // NEW: Better scroll handling for new messages
          if (newMessagesArrived || pendingOptimisticMessages.length > 0) {
            // Check if the new message is from the current user
            const isFromCurrentUser = newMessagesArrived && fetchedMessages.length > 0 && fetchedMessages[fetchedMessages.length - 1].senderId === currentUser?.uid;
            // Determine if we should scroll
            const shouldScroll = shouldForceScrollRef.current || newMessagesArrived && (isFromCurrentUser || userScrollPositionRef.current.isNearBottom);
            if (shouldScroll) {
              // Use a small delay to ensure the DOM has updated
              setTimeout(() => {
                if (isMountedRef.current) {
                  scrollToBottom(shouldForceScrollRef.current); // Force scroll if needed
                  if (shouldForceScrollRef.current) {
                    shouldForceScrollRef.current = false;
                  }
                }
              }, 100);
            }
          }
          return sortedMessages;
        });
      }
    });
    // Mark messages as read
    if (currentUser?.uid) {
      markMessagesAsRead(selectedConversation.id, currentUser.uid).catch(err => console.error('Error marking messages as read:', err));
    }
    // NEW: Add scroll event listener to track user scrolling
    const messageContainer = messageContainerRef.current;
    if (messageContainer) {
      messageContainer.addEventListener('scroll', updateScrollPosition);
    }
    return () => {
      unsubscribe();
      // Reset message count when unsubscribing
      lastMessageCountRef.current = 0;
      // Remove scroll event listener
      if (messageContainer) {
        messageContainer.removeEventListener('scroll', updateScrollPosition);
      }
    };
  }, [selectedConversation, currentUser, optimisticMessages]);
  // More consistent timestamp normalization with better fallbacks
  const getTimestampValue = (timestamp: any): number => {
    try {
      // For Firestore timestamps with toDate method
      if (timestamp && typeof timestamp === 'object' && timestamp.toDate) {
        try {
          return timestamp.toDate().getTime();
        } catch (e) {
          console.error('Error converting Firestore timestamp:', e);
          // Use a consistent fallback - beginning of today instead of current time
          return new Date().setHours(0, 0, 0, 0);
        }
      }
      // For Date objects
      if (timestamp instanceof Date) {
        return timestamp.getTime();
      }
      // For numeric timestamps (e.g., from optimistic messages using Date.now())
      if (typeof timestamp === 'number') {
        return timestamp;
      }
      // For "Just now" string from optimistic messages - use current time
      if (timestamp === 'Just now') {
        return Date.now();
      }
      // For other string timestamps, try to parse
      if (typeof timestamp === 'string') {
        // Try to extract time from strings like "2:30 PM"
        const timeMatch = timestamp.match(/(\d+):(\d+)\s*([AP]M)?/i);
        if (timeMatch) {
          const now = new Date();
          let hours = parseInt(timeMatch[1], 10);
          const minutes = parseInt(timeMatch[2], 10);
          const period = timeMatch[3]?.toUpperCase();
          // Convert to 24-hour format
          if (period === 'PM' && hours < 12) hours += 12;
          if (period === 'AM' && hours === 12) hours = 0;
          // Set the time but keep today's date
          now.setHours(hours, minutes, 0, 0);
          return now.getTime();
        }
        // Try to parse as a date string
        const parsedDate = new Date(timestamp);
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate.getTime();
        }
      }
      // Default fallback - use a fixed reference point instead of current time
      // This ensures stable sorting even with unparseable timestamps
      return new Date().setHours(0, 0, 0, 0); // beginning of today
    } catch (error) {
      console.error('Error in getTimestampValue:', error);
      // Ultimate fallback - use beginning of today as reference
      return new Date().setHours(0, 0, 0, 0);
    }
  };
  // More reliable message sorting with better error handling
  const sortMessagesByTimestamp = (messages: Message[]): Message[] => {
    try {
      return [...messages].sort((a, b) => {
        try {
          // Get numeric values for comparison
          const timeA = getTimestampValue(a.timestamp);
          const timeB = getTimestampValue(b.timestamp);
          // Sort by timestamp
          return timeA - timeB;
        } catch (error) {
          console.error('Error comparing messages:', error);
          // Return 0 to keep relative order in case of error
          return 0;
        }
      });
    } catch (error) {
      console.error('Error sorting messages:', error);
      // Return original array if sorting fails
      return [...messages];
    }
  };
  // NEW: Track scroll position to detect if user has manually scrolled
  const updateScrollPosition = () => {
    if (messageContainerRef.current) {
      const container = messageContainerRef.current;
      const {
        scrollTop,
        scrollHeight,
        clientHeight
      } = container;
      // Consider the user at the bottom if they're within 100px of the bottom
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      // Update the ref with current values
      userScrollPositionRef.current = {
        scrollTop,
        scrollHeight,
        clientHeight,
        isNearBottom,
        // Only set hasManuallyScrolled if they've scrolled away from bottom
        hasManuallyScrolled: userScrollPositionRef.current.hasManuallyScrolled ? !isNearBottom : false
      };
    }
  };
  // NEW: Completely rewritten scrollToBottom function for better reliability
  const scrollToBottom = (force = false) => {
    // If no container, exit early
    if (!messageContainerRef.current) return;
    // Don't scroll if user has manually scrolled up, unless forced
    if (!force && userScrollPositionRef.current.hasManuallyScrolled) return;
    // Generate a unique ID for this scroll operation
    const operationId = ++scrollOperationIdRef.current;
    // Use a multi-phase approach for more reliable scrolling
    // Phase 1: Immediate scroll attempt using requestAnimationFrame
    requestAnimationFrame(() => {
      // Check if this scroll operation is still valid
      if (operationId !== scrollOperationIdRef.current) return;
      if (messageContainerRef.current && isMountedRef.current) {
        const container = messageContainerRef.current;
        container.scrollTop = container.scrollHeight;
        // Phase 2: Follow-up scroll after a short delay
        setTimeout(() => {
          // Check if this scroll operation is still valid
          if (operationId !== scrollOperationIdRef.current) return;
          if (messageContainerRef.current && isMountedRef.current) {
            messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
            // Phase 3: Final check to ensure scroll position
            setTimeout(() => {
              // Check if this scroll operation is still valid
              if (operationId !== scrollOperationIdRef.current) return;
              if (messageContainerRef.current && isMountedRef.current) {
                const finalContainer = messageContainerRef.current;
                // If we're still not at the bottom, try one more time
                if (finalContainer.scrollHeight - finalContainer.scrollTop - finalContainer.clientHeight > 10) {
                  finalContainer.scrollTop = finalContainer.scrollHeight;
                }
                // Update scroll position tracking
                updateScrollPosition();
                // If this was a forced scroll, reset the manual scroll flag
                if (force) {
                  userScrollPositionRef.current.hasManuallyScrolled = false;
                }
              }
            }, 100);
          }
        }, 50);
      }
    });
  };
  // Handle conversation selection
  const handleSelectConversation = (conversation: Conversation) => {
    setSelectedConversation(conversation);
    setMessages([]);
    setOptimisticMessages([]);
    activeConversationIdRef.current = conversation.id;
    // Reset message count when selecting a new conversation
    lastMessageCountRef.current = 0;
    // NEW: Reset scroll tracking
    userScrollPositionRef.current = {
      scrollTop: 0,
      scrollHeight: 0,
      clientHeight: 0,
      isNearBottom: true,
      hasManuallyScrolled: false
    };
    // On mobile, hide the conversation list
    if (isMobileView) {
      setShowConversationList(false);
    }
    // Mark messages as read
    markMessagesAsRead(conversation.id, currentUser?.uid || '');
  };
  // NEW: Completely revised message sending with better scroll coordination
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() && !selectedFile || !selectedConversation || !currentUser || isSending) {
      return;
    }
    try {
      setIsSending(true);
      // Always force scroll after sending a message
      shouldForceScrollRef.current = true;
      // Reset manual scroll tracking - user wants to see their sent message
      userScrollPositionRef.current.hasManuallyScrolled = false;
      // Reset typing state
      setMobileState(prev => ({
        ...prev,
        isTyping: false
      }));
      // Create a local copy of the message text before clearing it
      const messageText = newMessage.trim() || undefined;
      const conversationId = selectedConversation.id;
      const senderId = currentUser.uid;
      const receiverId = selectedConversation.teacherId;
      // Log message details for debugging
      console.log('Preparing to send message:', {
        conversationId,
        senderId,
        receiverId,
        messageText: messageText || '(empty)',
        hasAttachment: !!selectedFile
      });
      // Verify we have all required fields
      if (!conversationId) {
        console.error('Missing conversationId for message send');
        throw new Error('Missing conversation ID');
      }
      if (!senderId) {
        console.error('Missing senderId for message send');
        throw new Error('Missing sender ID');
      }
      if (!receiverId) {
        console.error('Missing receiverId for message send');
        throw new Error('Missing receiver ID');
      }
      // Clear the input immediately for better UX
      setNewMessage('');
      // Create a unique temporary ID for the optimistic message
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      // Use a stable timestamp for optimistic messages
      const optimisticTimestamp = Date.now();
      // Create optimistic message
      const optimisticMessage: Message = {
        id: tempId,
        conversationId,
        senderId,
        receiverId,
        text: messageText,
        timestamp: optimisticTimestamp,
        read: false
      };
      // Add to optimistic messages state
      setOptimisticMessages(prev => [...prev, optimisticMessage]);
      // More reliable message merging
      setMessages(prevMessages => {
        const updatedMessages = [...prevMessages, optimisticMessage];
        return sortMessagesByTimestamp(updatedMessages);
      });
      // NEW: Better keyboard handling on mobile
      if (isMobileView) {
        // Don't blur input on iOS as it causes visual glitches
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        if (!isIOS && messageInputRef.current) {
          messageInputRef.current.blur();
        }
      }
      let attachment = undefined;
      if (selectedFile) {
        // Determine file type
        const fileType = selectedFile.type.startsWith('image/') ? 'image' : selectedFile.type.includes('pdf') ? 'pdf' : selectedFile.type.includes('audio') ? 'audio' : selectedFile.type.includes('video') ? 'video' : 'doc';
        attachment = {
          file: selectedFile,
          type: fileType as 'image' | 'pdf' | 'audio' | 'video' | 'doc'
        };
        console.log('Attachment details:', {
          fileName: selectedFile.name,
          fileType: selectedFile.type,
          fileSize: selectedFile.size,
          mappedType: fileType
        });
      }
      // NEW: Use a single scroll operation after message is added to DOM
      // This prevents multiple competing scroll attempts
      setTimeout(() => {
        if (isMountedRef.current) {
          scrollToBottom(true);
        }
      }, 50);
      // Send the message to Firestore
      console.log('Calling sendMessage with params:', {
        conversationId,
        senderId,
        receiverId,
        messageText: messageText || '(empty)',
        hasAttachment: !!attachment
      });
      try {
        const messageId = await sendMessage(conversationId, senderId, receiverId, messageText, attachment);
        console.log(`Message sent successfully with ID: ${messageId}`);
        // Remove the optimistic message from our tracking once we know it's been sent
        if (isMountedRef.current) {
          setOptimisticMessages(prev => prev.filter(msg => msg.id !== tempId));
        }
        // Clear file selection
        setSelectedFile(null);
        setFilePreview(null);
        // Clear any previous errors
        if (error) setError('');
      } catch (sendError) {
        console.error('Error in sendMessage function:', sendError);
        // Display the error to the user
        if (isMountedRef.current) {
          setError(`Failed to send message: ${sendError.message || 'Unknown error'}. Please try again.`);
          // Remove the optimistic message on error
          setOptimisticMessages(prev => prev.filter(msg => msg.id !== tempId));
          // Update messages to remove the optimistic message
          setMessages(prevMessages => prevMessages.filter(msg => msg.id !== tempId));
        }
        throw sendError; // Re-throw to be caught by the outer catch
      }
    } catch (err) {
      console.error('Error sending message:', err);
      // More detailed error logging
      if (err instanceof Error) {
        console.error('Error details:', err.message);
        console.error('Error stack:', err.stack);
      }
      if (isMountedRef.current) {
        // Show a more specific error message
        setError(err instanceof Error ? `Failed to send message: ${err.message}` : 'Failed to send message. Please try again.');
      }
    } finally {
      if (isMountedRef.current) {
        setIsSending(false);
        // NEW: One final scroll check after everything is complete
        // This is especially important for image attachments
        setTimeout(() => {
          if (isMountedRef.current && shouldForceScrollRef.current) {
            scrollToBottom(true);
            shouldForceScrollRef.current = false;
          }
        }, 300);
      }
    }
  };
  // NEW: Improved input change handler with better typing detection
  const handleMessageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewMessage(value);
    // Only update typing state when it changes
    const isCurrentlyTyping = value.trim() !== '';
    if (isCurrentlyTyping !== mobileState.isTyping) {
      setMobileState(prev => ({
        ...prev,
        isTyping: isCurrentlyTyping
      }));
    }
  };
  // NEW: Improved keyboard handling for mobile
  const handleInputFocus = () => {
    if (isMobileView) {
      // Update keyboard state
      setMobileState(prev => ({
        ...prev,
        isKeyboardOpen: true
      }));
      // Reset manual scroll tracking when keyboard opens
      userScrollPositionRef.current.hasManuallyScrolled = false;
      // Scroll to bottom with a delay to account for keyboard animation
      // Different delays for iOS vs Android
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const scrollDelay = isIOS ? 300 : 250;
      setTimeout(() => {
        if (isMountedRef.current) {
          scrollToBottom(true);
        }
      }, scrollDelay);
    }
  };
  // NEW: Improved keyboard handling for when input loses focus
  const handleInputBlur = () => {
    if (isMobileView) {
      // Use a short delay to ensure other actions complete first
      setTimeout(() => {
        if (isMountedRef.current) {
          setMobileState(prev => ({
            ...prev,
            isKeyboardOpen: false,
            // Only keep typing state if there's text
            isTyping: newMessage.trim() !== ''
          }));
        }
      }, 100);
    }
  };
  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      // Check file size for mobile (5MB limit for mobile vs 10MB for desktop)
      const maxSize = isMobileView ? 5 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        const sizeInMB = maxSize / (1024 * 1024);
        alert(`File size exceeds ${sizeInMB}MB limit on ${isMobileView ? 'mobile' : 'desktop'}`);
        return;
      }
      setSelectedFile(file);
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = e => {
          if (isMountedRef.current) {
            setFilePreview(e.target?.result as string);
          }
        };
        reader.readAsDataURL(file);
      } else {
        setFilePreview(null);
      }
    }
  };
  // Handle file removal
  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  // Handle back button on mobile
  const handleBackToList = () => {
    setShowConversationList(true);
  };
  // More reliable timestamp formatting
  const formatTime = (timestamp: any) => {
    try {
      // Handle string timestamps
      if (typeof timestamp === 'string') {
        // If it's already formatted (like "2:30 PM"), return it directly
        if (/\d+:\d+\s*([AP]M)?/i.test(timestamp)) {
          return timestamp;
        }
        // Otherwise try to parse it
        const date = new Date(timestamp);
        if (!isNaN(date.getTime())) {
          return date.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          });
        }
        // If parsing fails, return as is
        return timestamp;
      }
      // Handle numeric timestamps (from optimistic messages)
      if (typeof timestamp === 'number') {
        return new Date(timestamp).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      // Handle Firestore timestamps
      if (timestamp && typeof timestamp.toDate === 'function') {
        try {
          return timestamp.toDate().toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          });
        } catch (e) {
          console.error('Error formatting Firestore timestamp:', e);
          return 'Just now';
        }
      }
      // Default fallback
      return 'Just now';
    } catch (error) {
      console.error('Error in formatTime:', error);
      return 'Just now';
    }
  };
  // Check if the user is a student
  useEffect(() => {
    const checkUserType = async () => {
      if (currentUser) {
        // Here you would typically check if the user is a student
        // For now, we'll assume they are
      }
    };
    checkUserType();
  }, [currentUser]);
  // NEW: More reliable initial scroll when conversation changes
  useEffect(() => {
    if (selectedConversation) {
      // Reset message count
      lastMessageCountRef.current = 0;
      // Reset scroll tracking
      userScrollPositionRef.current = {
        scrollTop: 0,
        scrollHeight: 0,
        clientHeight: 0,
        isNearBottom: true,
        hasManuallyScrolled: false
      };
      // Schedule a scroll after the view has rendered
      // Use a sequence of attempts to ensure it works
      const scrollAttempts = [100, 300, 600];
      scrollAttempts.forEach(delay => {
        setTimeout(() => {
          if (isMountedRef.current && selectedConversation) {
            scrollToBottom(true);
          }
        }, delay);
      });
    }
  }, [selectedConversation]);
  // NEW: Handle mobile orientation changes
  useEffect(() => {
    const handleOrientationChange = () => {
      if (isMobileView) {
        // Update viewport height
        setMobileState(prev => ({
          ...prev,
          viewportHeight: window.innerHeight
        }));
        // Scroll to bottom after orientation change completes
        setTimeout(() => {
          if (isMountedRef.current && selectedConversation) {
            scrollToBottom(true);
          }
        }, 300);
      }
    };
    window.addEventListener('orientationchange', handleOrientationChange);
    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [isMobileView, selectedConversation]);
  // Redirect if not logged in
  if (!currentUser) {
    navigate('/student-login');
    return null;
  }
  // Define text color classes based on theme
  const textColorClass = isDark ? 'text-white' : 'text-gray-800';
  const subtextColorClass = isDark ? 'text-gray-300' : 'text-gray-600';
  const lightTextColorClass = isDark ? 'text-gray-300' : 'text-gray-500';
  const extraLightTextColorClass = isDark ? 'text-gray-400' : 'text-gray-500';
  // Define background classes based on theme
  const mainBgClass = 'bg-transparent';
  const containerBgClass = isDark ? 'bg-white/10' : 'bg-white';
  const borderClass = isDark ? 'border-white/10' : 'border-gray-200';
  const conversationItemHoverClass = isDark ? 'hover:bg-white/5' : 'hover:bg-gray-100';
  const conversationItemSelectedClass = isDark ? 'bg-white/10' : 'bg-gray-100';
  const emptyStateBgClass = isDark ? 'bg-white/5' : 'bg-gray-50';
  const inputBgClass = isDark ? 'bg-white/5' : 'bg-white';
  const inputBorderClass = isDark ? 'border-white/10' : 'border-gray-300';
  const inputTextClass = isDark ? 'text-white' : 'text-gray-800';
  const iconClass = isDark ? 'text-gray-400' : 'text-gray-500';
  const iconHoverClass = isDark ? 'hover:text-white' : 'hover:text-gray-800';
  const filePreviewBgClass = isDark ? 'bg-white/5' : 'bg-gray-100';
  const fileIconBgClass = isDark ? 'bg-white/10' : 'bg-gray-200';
  const errorBgClass = isDark ? 'bg-red-500/20' : 'bg-red-100';
  const errorBorderClass = isDark ? 'border-red-500/30' : 'border-red-200';
  const errorTextClass = isDark ? 'text-white' : 'text-red-800';
  const avatarBgClass = isDark ? 'bg-white/10' : 'bg-gray-200';
  const messageBubbleFromOtherClass = isDark ? 'bg-white/10 text-white' : 'bg-gray-200 text-gray-800';
  const messageBubbleTimeClass = isDark ? 'text-white/70' : 'text-gray-500';
  return <div className={`w-full min-h-screen ${mainBgClass} ${textColorClass} font-sans transition-colors duration-300`} data-id="element-7144">
      <Navbar data-id="element-7145" />
      <main className="pt-20 pb-16" data-id="element-7146">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-7147">
          <div className="flex items-center justify-between mb-6" data-id="element-7148">
            <h1 className={`text-3xl md:text-4xl font-serif font-bold ${textColorClass}`} data-id="element-7149">
              Messages
            </h1>
          </div>
          {loading ? <div className="flex justify-center items-center py-12" data-id="element-7150">
              <div className={`animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 ${isDark ? 'border-white' : 'border-gray-800'}`} data-id="element-7151"></div>
            </div> : error ? <div className={`${errorBgClass} border ${errorBorderClass} rounded-lg p-4 mb-6`} data-id="element-7152">
              <p className={errorTextClass} data-id="element-7153">{error}</p>
            </div> : <div className={`${containerBgClass} backdrop-blur-sm rounded-xl overflow-hidden border ${borderClass}`} data-id="element-7154">
              <div className="flex h-[600px]" data-id="element-7155">
                {/* Conversation List - only shown on desktop or when showConversationList is true on mobile */}
                {(!isMobileView || showConversationList) && <div className={`w-full md:w-1/3 border-r ${borderClass} overflow-y-auto`} data-id="element-7156">
                    <div className={`p-4 border-b ${borderClass}`} data-id="element-7157">
                      <h2 className={`text-lg font-medium ${textColorClass}`} data-id="element-7158">
                        Conversations
                      </h2>
                    </div>
                    {conversations.length === 0 ? <div className="p-6 text-center" data-id="element-7159">
                        <p className={lightTextColorClass} data-id="element-7160">
                          No conversations yet
                        </p>
                        <p className={`text-sm ${extraLightTextColorClass} mt-2`} data-id="element-7161">
                          Find a teacher to start a conversation
                        </p>
                        <button onClick={() => navigate('/find-teachers')} className="mt-4 bg-deep-pink hover:bg-burgundy text-white px-4 py-2 rounded-lg text-sm" data-id="element-7162">
                          Find Teachers
                        </button>
                      </div> : <ul data-id="element-7163">
                        {conversations.map(conversation => <li key={conversation.id} className={`border-b ${borderClass} cursor-pointer transition-colors ${selectedConversation?.id === conversation.id ? conversationItemSelectedClass : conversationItemHoverClass}`} onClick={() => handleSelectConversation(conversation)} data-id="element-7164">
                            <div className="p-4 flex items-center" data-id="element-7165">
                              <div className={`h-12 w-12 rounded-full overflow-hidden ${avatarBgClass} flex-shrink-0 mr-3`} data-id="element-7166">
                                {conversation.teacherAvatar ? <img src={conversation.teacherAvatar} alt={conversation.teacherName} className="h-full w-full object-cover" data-id="element-7167" /> : <div className={`h-full w-full flex items-center justify-center ${isDark ? 'bg-gray-700' : 'bg-gray-300'}`} data-id="element-7168">
                                    <UserIcon className={`h-6 w-6 ${iconClass}`} data-id="element-7169" />
                                  </div>}
                              </div>
                              <div className="flex-1 min-w-0" data-id="element-7170">
                                <div className="flex justify-between items-center mb-1" data-id="element-7171">
                                  <h3 className={`${textColorClass} font-medium truncate`} data-id="element-7172">
                                    {conversation.teacherName}
                                  </h3>
                                  <span className={`text-xs ${extraLightTextColorClass}`} data-id="element-7173">
                                    {conversation.lastMessageTimestamp ? formatTime(conversation.lastMessageTimestamp) : ''}
                                  </span>
                                </div>
                                <div className="flex items-center" data-id="element-7174">
                                  {conversation.instrument && <span className="text-xs bg-deep-pink/20 text-deep-pink px-2 py-0.5 rounded-full mr-2" data-id="element-7175">
                                      {conversation.instrument}
                                    </span>}
                                  <p className={`text-sm ${lightTextColorClass} truncate`} data-id="element-7176">
                                    {conversation.lastMessage || 'Start a conversation'}
                                  </p>
                                  {conversation.unread && <span className="ml-2 h-2 w-2 bg-deep-pink rounded-full" data-id="element-7177"></span>}
                                </div>
                              </div>
                            </div>
                          </li>)}
                      </ul>}
                  </div>}
                {/* Message Area - only shown on desktop or when showConversationList is false on mobile */}
                {(!isMobileView || !showConversationList) && <div className="w-full md:w-2/3 flex flex-col" data-id="element-7178">
                    {selectedConversation ? <>
                        {/* Conversation Header */}
                        <div className={`p-4 border-b ${borderClass} flex items-center`} data-id="element-7179">
                          {isMobileView && <button onClick={handleBackToList} className={`mr-2 ${iconClass} ${iconHoverClass}`} data-id="element-7180">
                              <ChevronLeftIcon className="h-5 w-5" data-id="element-7181" />
                            </button>}
                          <div className={`h-10 w-10 rounded-full overflow-hidden ${avatarBgClass} mr-3`} data-id="element-7182">
                            {selectedConversation.teacherAvatar ? <img src={selectedConversation.teacherAvatar} alt={selectedConversation.teacherName} className="h-full w-full object-cover" data-id="element-7183" /> : <div className={`h-full w-full flex items-center justify-center ${isDark ? 'bg-gray-700' : 'bg-gray-300'}`} data-id="element-7184">
                                <UserIcon className={`h-5 w-5 ${iconClass}`} data-id="element-7185" />
                              </div>}
                          </div>
                          <div className="flex-1" data-id="element-7186">
                            <h3 className={`${textColorClass} font-medium`} data-id="element-7187">
                              {selectedConversation.teacherName}
                            </h3>
                            {selectedConversation.instrument && <p className={`text-xs ${extraLightTextColorClass}`} data-id="element-7188">
                                {selectedConversation.instrument} Teacher
                              </p>}
                          </div>
                          <div className="flex space-x-3" data-id="element-7189">
                            <button className={`${iconClass} ${iconHoverClass}`} data-id="element-7190">
                              <PhoneIcon className="h-5 w-5" data-id="element-7191" />
                            </button>
                            <button className={`${iconClass} ${iconHoverClass}`} data-id="element-7192">
                              <VideoIcon className="h-5 w-5" data-id="element-7193" />
                            </button>
                            <button className={`${iconClass} ${iconHoverClass}`} data-id="element-7194">
                              <InfoIcon className="h-5 w-5" data-id="element-7195" />
                            </button>
                          </div>
                        </div>
                        {/* Messages */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-4" ref={messageContainerRef} data-id="element-7196">
                          {messages.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-center" data-id="element-7197">
                              <div className={`${emptyStateBgClass} rounded-full p-4 mb-3`} data-id="element-7198">
                                <MessageSquareIcon className={`h-8 w-8 ${iconClass}`} data-id="element-7199" />
                              </div>
                              <h3 className={`${textColorClass} font-medium mb-1`} data-id="element-7200">
                                Start a conversation
                              </h3>
                              <p className={`text-sm ${extraLightTextColorClass} max-w-xs`} data-id="element-7201">
                                Send a message to{' '}
                                {selectedConversation.teacherName} to discuss
                                lessons or ask questions.
                              </p>
                            </div> : messages.map(message => {
                    // Determine if message is from current user (student)
                    const isFromCurrentUser = message.senderId === currentUser.uid;
                    // Check if it's an optimistic message (has a temp- id)
                    const isOptimistic = typeof message.id === 'string' && message.id.startsWith('temp-');
                    return <div key={message.id} className={`flex ${isFromCurrentUser ? 'justify-end' : 'justify-start'}`} data-id="element-7202">
                                  <div className={`max-w-[75%] rounded-lg px-4 py-2 ${isFromCurrentUser ? 'bg-deep-pink text-white' : messageBubbleFromOtherClass} ${isOptimistic ? 'opacity-80' : ''}`} data-id="element-7203">
                                    {message.text && <p data-id="element-7204">{message.text}</p>}
                                    {message.attachmentUrl && <div className="mt-2" data-id="element-7205">
                                        {message.attachmentType === 'image' ? <img src={message.attachmentUrl} alt="Attachment" className="max-w-full rounded"
                          // NEW: Force scroll when image loads to handle layout shifts
                          onLoad={() => {
                            if (isFromCurrentUser) {
                              scrollToBottom(true);
                            }
                          }} data-id="element-7206" /> : <a href={message.attachmentUrl} target="_blank" rel="noopener noreferrer" className={`flex items-center ${filePreviewBgClass} rounded p-2 text-sm`} data-id="element-7207">
                                            <FileIcon className="h-4 w-4 mr-2" data-id="element-7208" />
                                            <span className="truncate" data-id="element-7209">
                                              {message.attachmentName || 'Attachment'}
                                            </span>
                                          </a>}
                                      </div>}
                                    <div className={`text-xs mt-1 ${isFromCurrentUser ? messageBubbleTimeClass : extraLightTextColorClass}`} data-id="element-7210">
                                      {formatTime(message.timestamp)}
                                      {isOptimistic && ' (sending...)'}
                                    </div>
                                  </div>
                                </div>;
                  })}
                          <div ref={messagesEndRef} data-id="element-7211" />
                        </div>
                        {/* Message Input - Must always be visible on mobile */}
                        <div className={`p-4 border-t ${borderClass}`} data-id="element-7212">
                          {selectedFile && <div className={`mb-2 ${filePreviewBgClass} rounded-lg p-2 flex items-center`} data-id="element-7213">
                              {filePreview ? <img src={filePreview} alt="Preview" className="h-16 w-16 object-cover rounded mr-2" data-id="element-7214" /> : <div className={`h-16 w-16 ${fileIconBgClass} rounded flex items-center justify-center mr-2`} data-id="element-7215">
                                  <FileIcon className={`h-6 w-6 ${iconClass}`} data-id="element-7216" />
                                </div>}
                              <div className="flex-1 min-w-0" data-id="element-7217">
                                <p className={`text-sm ${textColorClass} truncate`} data-id="element-7218">
                                  {selectedFile.name}
                                </p>
                                <p className={`text-xs ${extraLightTextColorClass}`} data-id="element-7219">
                                  {(selectedFile.size / 1024).toFixed(1)} KB
                                </p>
                              </div>
                              <button onClick={handleRemoveFile} className={`${iconClass} ${iconHoverClass}`} data-id="element-7220">
                                <XIcon className="h-5 w-5" data-id="element-7221" />
                              </button>
                            </div>}
                          <form onSubmit={handleSendMessage} className="flex items-center" data-id="element-7222">
                            <button type="button" className={`${iconClass} ${iconHoverClass} mr-2`} onClick={() => fileInputRef.current?.click()} disabled={isSending} data-id="element-7223">
                              <PaperclipIcon className="h-5 w-5" data-id="element-7224" />
                            </button>
                            <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.mp3,.mp4" data-id="element-7225" />
                            <input type="text" ref={messageInputRef} value={newMessage} onChange={handleMessageInputChange} onFocus={handleInputFocus} onBlur={handleInputBlur} placeholder={isSending ? 'Sending...' : 'Type a message...'} className={`flex-1 ${inputBgClass} border ${inputBorderClass} rounded-lg px-4 py-2 ${inputTextClass} focus:outline-none focus:ring-2 focus:ring-deep-pink`} disabled={isSending} data-id="element-7226" />
                            <button type="submit" disabled={!newMessage.trim() && !selectedFile || isSending} className={`ml-2 bg-deep-pink hover:bg-burgundy text-white rounded-full p-2 ${!newMessage.trim() && !selectedFile || isSending ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-7227">
                              <SendIcon className="h-5 w-5" data-id="element-7228" />
                            </button>
                          </form>
                        </div>
                      </> : <div className="h-full flex flex-col items-center justify-center text-center p-6" data-id="element-7229">
                        <div className={`${emptyStateBgClass} rounded-full p-6 mb-4`} data-id="element-7230">
                          <MessageSquareIcon className={`h-10 w-10 ${iconClass}`} data-id="element-7231" />
                        </div>
                        <h3 className={`text-xl font-medium ${textColorClass} mb-2`} data-id="element-7232">
                          Your Messages
                        </h3>
                        <p className={`${subtextColorClass} max-w-md mb-6`} data-id="element-7233">
                          Select a conversation from the list or find a teacher
                          to start messaging.
                        </p>
                        <button onClick={() => navigate('/find-teachers')} className="bg-deep-pink hover:bg-burgundy text-white px-6 py-2 rounded-lg" data-id="element-7234">
                          Find Teachers
                        </button>
                      </div>}
                  </div>}
              </div>
            </div>}
        </div>
      </main>
      <Footer data-id="element-7235" />
    </div>;
};